import type { Category, Product } from '@agos/api-client/model'
import { T, formatCount, formatMoney, tt } from '@agos/shared'
import { SearchInput, cn } from '@agos/ui'
import { useMemo, useState, type ReactNode } from 'react'

import {
  checklistGroups,
  idsOf,
  tickedCount,
  toggled,
  withAll,
  withNone,
} from './upsell-rules'

/**
 * Products as a checklist under their menu categories - `FRONTEND.md` §9.711.
 *
 * The one control the screen uses three times: the products an add-on is offered with, the products a group's
 * item is offered with, and the targets of *Kopírovať doplnky*. **Grouped by category with *všetky / žiadne* per
 * category** because that is how the operator thinks of it (*"all the pizzas get the dip"*), and the search is the
 * way to the one product that is not where they expected.
 *
 * **Every product renders, no cap** - §9.227 measured a capped list hiding 104 of 164 products with nothing on
 * screen saying so. The box scrolls instead.
 */
export function ProductChecklist({
  products,
  categories,
  excluded,
  selected,
  onChange,
  marker,
  note,
}: {
  products: readonly Product[]
  categories: readonly Category[]
  /** Products that may not be ticked here at all - the add-on itself, the copy's source. */
  excluded: ReadonlySet<number>
  selected: ReadonlySet<number>
  onChange: (next: Set<number>) => void
  /** `data-{marker}-check`, `-all`, `-none` - what the e2e selects by. */
  marker: string
  /** An extra word beside a product, such as the group it is linked under now. */
  note?: (product: Product) => ReactNode
}) {
  const [search, setSearch] = useState('')
  const groups = useMemo(
    () => checklistGroups(products, categories, search, excluded),
    [products, categories, search, excluded],
  )

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={tt('Hľadať produkt...')}
          className="flex-1 min-w-[12rem]"
        />
        <p className="text-xs text-muted-foreground" {...{ [`data-${marker}-count`]: selected.size }}>
          {formatCount(selected.size)} <T>vybraných</T>
        </p>
      </div>
      <div className="max-h-[28rem] overflow-y-auto rounded-xl border border-border">
        {groups.length === 0 && (
          <p className="py-4 text-center text-xs text-muted-foreground">
            <T>Žiadne produkty</T>
          </p>
        )}
        {groups.map((group) => {
          const ids = idsOf(group)
          const ticked = tickedCount(selected, ids)
          return (
            <section key={group.key} {...{ [`data-${marker}-category`]: group.key }}>
              <div className="sticky top-0 z-[1] flex items-center gap-2 bg-muted px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span className="flex-1 truncate">
                  <T>{group.category}</T>
                </span>
                <span className="tabular-nums normal-case">
                  {formatCount(ticked)} / {formatCount(ids.length)}
                </span>
                <button
                  type="button"
                  className="rounded-md px-1.5 py-0.5 normal-case text-primary hover:bg-primary/10"
                  {...{ [`data-${marker}-all`]: group.key }}
                  onClick={() => onChange(withAll(selected, ids))}
                >
                  <T>všetky</T>
                </button>
                <button
                  type="button"
                  className="rounded-md px-1.5 py-0.5 normal-case text-primary hover:bg-primary/10"
                  {...{ [`data-${marker}-none`]: group.key }}
                  onClick={() => onChange(withNone(selected, ids))}
                >
                  <T>žiadne</T>
                </button>
              </div>
              {group.products.map((product) => {
                const id = product.id as number
                const checked = selected.has(id)
                return (
                  <label
                    key={id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 border-t border-border/40 px-3 py-1.5 text-sm',
                      checked ? 'bg-primary/5' : 'hover:bg-muted/30',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onChange(toggled(selected, id))}
                      className="h-4 w-4 rounded accent-primary"
                      {...{ [`data-${marker}-check`]: id }}
                    />
                    <span className={cn('flex-1 truncate', product.active === false && 'text-muted-foreground')}>
                      <T>{product.name}</T>
                      {product.active === false && (
                        <span className="ml-1 text-xs">
                          (<T>vypnutý</T>)
                        </span>
                      )}
                    </span>
                    {note?.(product)}
                    {/* The price tells two similarly named products apart, as in *Doplnky k produktu*. */}
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {formatMoney(product.price ?? 0)}
                    </span>
                  </label>
                )
              })}
            </section>
          )
        })}
      </div>
    </div>
  )
}
